import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  TV_BRACKET_AUTOSCROLL_INTERVAL_MS,
  TV_BRACKET_AUTOSCROLL_PAGE_RATIO,
  TV_BRACKET_AUTOSCROLL_TRANSITION_MS,
} from './tv-mode.constants';

/**
 * Viewport con auto-scroll vertical por páginas cuando el contenido supera la
 * altura disponible. Avanza un "page step" (= viewport * page ratio) cada
 * `intervalMs` y al llegar al final vuelve a 0. Si el contenido entra completo
 * se renderiza estático sin animación.
 *
 * Usado por el cuadro eliminatorio y por la lista de "Próximos partidos".
 */
export const TVAutoScrollViewport = ({
  children,
  intervalMs = TV_BRACKET_AUTOSCROLL_INTERVAL_MS,
  transitionMs = TV_BRACKET_AUTOSCROLL_TRANSITION_MS,
  pageRatio = TV_BRACKET_AUTOSCROLL_PAGE_RATIO,
}: {
  children: React.ReactNode;
  intervalMs?: number;
  transitionMs?: number;
  pageRatio?: number;
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [viewportH, setViewportH] = useState(0);
  const [contentH, setContentH] = useState(0);
  const [transitionOn, setTransitionOn] = useState(false);

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? 0;
      setViewportH(h);
    });
    ro.observe(vp);
    setViewportH(vp.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const tr = trackRef.current;
    if (!tr) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? 0;
      setContentH(h);
    });
    ro.observe(tr);
    setContentH(tr.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, []);

  const overflow = Math.max(0, contentH - viewportH);
  const pageStep = Math.max(1, viewportH * pageRatio);
  const offsets = useMemo(() => {
    if (overflow <= 0 || viewportH <= 0) return [0];
    const out: number[] = [0];
    for (let o = pageStep; o < overflow; o += pageStep) {
      out.push(o);
    }
    if (out[out.length - 1] !== overflow) out.push(overflow);
    return out;
  }, [overflow, pageStep, viewportH]);

  const [page, setPage] = useState(0);

  useEffect(() => {
    if (page >= offsets.length) setPage(0);
  }, [offsets.length, page]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setTransitionOn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (offsets.length <= 1) return;
    const id = setInterval(() => {
      setPage((p) => (p + 1) % offsets.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [offsets.length, intervalMs]);

  const translateY = -(offsets[Math.min(page, offsets.length - 1)] ?? 0);
  const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';

  return (
    <div
      ref={viewportRef}
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        ref={trackRef}
        style={{
          transform: `translate3d(0, ${translateY}px, 0)`,
          transition: transitionOn
            ? `transform ${transitionMs}ms ${ease}`
            : 'none',
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </div>
  );
};
